import React, { useMemo, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSalesFormStore } from "../../services/useSalesFormStore";
import {
  capitalizeFirstLetterOfEachWord,
  buildTransactionDocumentFileName,
  convertToUpperCase,
  formatFileUrlValue,
} from "../../utils/Formatter";
import DownloadButton from "../../components/DownloadButton";
import { JQInvoiceLayout } from "../../components/transactionPrintLayouts/JQInvoiceLayout";
import { MRInvoiceLayout } from "../../components/transactionPrintLayouts/MRInvoiceLayout";
import { MaterialIssueInvoiceLayout } from "../../components/transactionPrintLayouts/MaterialIssueInvoiceLayout";
import { DCInvoiceLayout } from "../../components/transactionPrintLayouts/DCInvoiceLayout";
import { PEInvoiceLayout } from "../../components/transactionPrintLayouts/PEInvoiceLayout";
import { POInvoiceLayout } from "../../components/transactionPrintLayouts/POInvoiceLayout";
import { SQInvoiceLayout } from "../../components/transactionPrintLayouts/SQInvoiceLayout";
import { InspectionReport } from "../../components/transactionPrintLayouts/InspectionReport";
import { IncomingInspectionInvoiceLayout } from "../../components/transactionPrintLayouts/IncomingInspectionInvoiceLayout";
import { InProcessInspectionInvoiceLayout } from "../../components/transactionPrintLayouts/InProcessInspectionInvoiceLayout";
import { FinalInspectionInvoiceLayout } from "../../components/transactionPrintLayouts/FinalInspectionInvoiceLayout";
import { SalesInvoiceLayout } from "../../components/transactionPrintLayouts/SalesInvoiceLayout";
import { SalesEnquiryLayout } from "../../components/transactionPrintLayouts/SalesEnquiryLayout";
import { CustomerPOInvoice } from "../../components/transactionPrintLayouts/CustomerPOInvoice";
import { PIInvoiceLayout } from "../../components/transactionPrintLayouts/PIInvoiceLayout";
import { SIInvoiceLayout } from "../../components/transactionPrintLayouts/SIInvoiceLayout";
import { JobCardInvoiceLayout } from "../../components/transactionPrintLayouts/JobCardInvoiceLayout";
import { PQInvoiceLayout } from "../../components/transactionPrintLayouts/PQInvoiceLayout";
import { JobWorkInvoiceLayout } from "../../components/transactionPrintLayouts/JobWorkInvoiceLayout";
import { PurchaseInvoiceLayout } from "../../components/transactionPrintLayouts/PurchaseInvoiceLayout";
import { WorkOrderInvoiceLayout } from "../../components/transactionPrintLayouts/WorkOrderInvoiceLayout";
import { JobWorkOrderInvoiceLayout } from "../../components/transactionPrintLayouts/JobWorkOrderInvoiceLayout";
import { PurchaseIndentInvoiceLayout } from "../../components/transactionPrintLayouts/PurchaseIndentInvoiceLayout";
import { JobWorkEnquiryInvoiceLayout } from "../../components/transactionPrintLayouts/JobWorkEnquiryInvoiceLayout";
import { GoodsReceiptNoteInvoiceLayout } from "../../components/transactionPrintLayouts/GoodsReceiptNoteInvoiceLayout";
import { MRMInvoiceLayout } from "../../components/transactionPrintLayouts/MRMInvoiceLayout";
import { VPInvoiceLayout } from "../../components/transactionPrintLayouts/VPInvoiceLayout";
import { RouteCardInvoiceLayout } from "../../components/transactionPrintLayouts/RouteCardInvoiceLayout";
import { ProcessCardInvoiceLayout } from "../../components/transactionPrintLayouts/ProcessCardInvoiceLayout";
import { ContractReviewFormLayout } from "../../components/transactionPrintLayouts/ContractReviewFormLayout";
import { TrainingAttendanceLayout } from "../../components/transactionPrintLayouts/TrainingAttendanceLayout";
import { ListOfEmployeesLayout } from "../../components/transactionPrintLayouts/ListOfEmployeesLayout";
import { ListOfTrainersLayout } from "../../components/transactionPrintLayouts/ListOfTrainersLayout";
import { HrInductionTrainingSheetLayout } from "../../components/transactionPrintLayouts/HrInductionTrainingSheetLayout";
import { HrTrainingNeedsRecordLayout } from "../../components/transactionPrintLayouts/HrTrainingNeedsRecordLayout";
import { HrEmployeeHistoryCardLayout } from "../../components/transactionPrintLayouts/HrEmployeeHistoryCardLayout";
import { ECInvoiceLayout } from "../../components/transactionPrintLayouts/ECInvoiceLayout";
import { SupplierEvaluationFormLayout } from "../../components/transactionPrintLayouts/SupplierEvaluationFormLayout";
import "../../styles/pages/invoice/Invoice.css";
import { getFieldMaster } from "../../apis/fieldMaster";
import {
  fetchAttachments,
  filterAttachmentsForRowValue,
} from "../../apis/attachments";

const Invoice = () => {
  const { tab, category } = useParams();
  const location = useLocation();
  const storeFormData = useSalesFormStore((state) => state.formData);
  const navigationFormData = location.state?.formData;
  const selectedReportName = location.state?.selectedReportName;

  const companyData = useMemo(
    () => navigationFormData?.companyData || storeFormData?.companyData || {},
    [navigationFormData, storeFormData],
  );
  const rowData = useMemo(
    () => navigationFormData?.rowData || storeFormData?.rowData || [],
    [navigationFormData, storeFormData],
  );

  const { headDetails, itemDetails: rawItemDetails } = useMemo(() => {
    if (navigationFormData) {
      return {
        headDetails: navigationFormData.headDetails || {},
        itemDetails: navigationFormData.itemDetails || [],
      };
    }
    return {
      headDetails: storeFormData?.headDetails || {},
      itemDetails: storeFormData?.itemDetails || [],
    };
  }, [navigationFormData, storeFormData]);
  const [resolvedRowFields, setResolvedRowFields] = useState([]);
  const [resolvedHeadFields, setResolvedHeadFields] = useState([]);
  const transactionCode =
    navigationFormData?.transactionCode ||
    navigationFormData?.headDetails?.tran_code ||
    storeFormData?.headDetails?.tran_code;

  const [itemAttachments, setItemAttachments] = useState({});

  useEffect(() => {
    const parentIds = [
      ...new Set(
        rawItemDetails.map((item) => item?.tran_id).filter((id) => id != null),
      ),
    ];
    if (!transactionCode || !parentIds.length) {
      setItemAttachments({});
      return;
    }

    let isMounted = true;
    Promise.all(
      parentIds.map((parentId) =>
        fetchAttachments({
          tranCode: transactionCode,
          scope: "item",
          parentId,
        }).catch(() => []),
      ),
    ).then((responses) => {
      if (!isMounted) return;
      const grouped = {};
      responses.forEach((response, idx) => {
        const attachments = Array.isArray(response)
          ? response
          : response?.attachments || response?.items || response?.data || [];
        attachments.forEach((attachment) => {
          if (!attachment?.field_name) return;
          const key = `${parentIds[idx]}:${attachment.field_name}`;
          (grouped[key] = grouped[key] || []).push(attachment);
        });
      });
      setItemAttachments(grouped);
    });

    return () => {
      isMounted = false;
    };
  }, [rawItemDetails, transactionCode]);

  const itemDetails = useMemo(() => {
    if (!Object.keys(itemAttachments).length) return rawItemDetails;
    return rawItemDetails.map((item) => {
      if (item?.tran_id == null) return item;
      const updated = { ...item };
      Object.entries(itemAttachments).forEach(([key, attachments]) => {
        const [parentId, fieldName] = key.split(/:(.+)/);
        if (String(item.tran_id) !== parentId) return;
        const names = filterAttachmentsForRowValue(attachments, item[fieldName])
          .map((a) => a.file_name || a.name)
          .filter(Boolean);
        if (names.length) updated[fieldName] = names.join(", ");
      });
      return updated;
    });
  }, [rawItemDetails, itemAttachments]);

  useEffect(() => {
    let isMounted = true;

    const hydrateFromFieldMaster = async () => {
      try {
        const tranCode = transactionCode;
        if (!tranCode) return;
        const resp = await getFieldMaster({ transactionCode: tranCode });
        if (resp?.items?.head_fields && Array.isArray(resp.items.head_fields)) {
          const headFields = resp.items.head_fields
            .filter((f) => !f.is_internal_field)
            .sort((a, b) => (a.order_no || 0) - (b.order_no || 0));
          if (isMounted) setResolvedHeadFields(headFields);
        }

        if (!Array.isArray(rowData) || rowData.length === 0) {
          if (
            resp?.items?.item_fields &&
            Array.isArray(resp.items.item_fields)
          ) {
            const fields = resp.items.item_fields
              .filter((f) => !f.is_internal_field)
              .sort((a, b) => (a.order_no || 0) - (b.order_no || 0));
            if (isMounted) setResolvedRowFields(fields);
            return;
          }
        }
      } catch (_) {}
      const sample = (Array.isArray(rawItemDetails) && rawItemDetails[0]) || {};
      const keys = Object.keys(sample);
      if (isMounted && (!Array.isArray(rowData) || rowData.length === 0)) {
        setResolvedHeadFields([]);
        setResolvedRowFields(
          keys.map((key) => ({
            field_name: key,
            title: key,
            is_internal_field: false,
          })),
        );
      }
    };

    if (Array.isArray(rowData) && rowData.length > 0) {
      setResolvedRowFields(rowData);
    }

    hydrateFromFieldMaster();

    return () => {
      isMounted = false;
    };
  }, [rowData, rawItemDetails, transactionCode]);

  const invoiceTableData = useMemo(() => {
    if (!resolvedRowFields.length || !itemDetails.length) {
      return null;
    }

    const visibleFields = resolvedRowFields.filter(
      (field) => !field.is_internal_field,
    );

    return {
      headings: visibleFields.map((field) => ({
        display_name: field.title || field.field_name,
        field_name: field.field_name,
        is_internal: false,
      })),
      downloadFileName: buildTransactionDocumentFileName(headDetails),
      rows: itemDetails.map((row) =>
        Object.fromEntries(
          Object.entries(row || {}).map(([key, val]) => [
            key,
            formatFileUrlValue(val),
          ]),
        ),
      ),
    };
  }, [resolvedRowFields, itemDetails, headDetails]);

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "N/A";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0.00";
    return Number(amount).toFixed(2);
  };

  const selectedReportNameValue = String(
    selectedReportName || "",
  ).toLowerCase();
  const isContractReviewForm =
    navigationFormData?.transactionCode === "CONTRACT_REVIEW_FORM" ||
    navigationFormData?.transactionCode === "CRF" ||
    selectedReportNameValue.includes("contract review");

  const isTrainingAttendanceForm =
    navigationFormData?.transactionCode === "HT_TRAINING_ATTENDANCE" ||
    navigationFormData?.transactionCode === "HT" ||
    navigationFormData?.transactionCode === "TA" ||
    String(category || "")
      .toLowerCase()
      .includes("training-attendence") ||
    String(category || "")
      .toLowerCase()
      .includes("training-attendance") ||
    selectedReportNameValue.includes("training attendence") ||
    selectedReportNameValue.includes("training attendance");

  const isListOfEmployeesForm =
    [
      "HR_LIST_OF_EMPLOYEES",
      "LIST_OF_EMPLOYEES",
      "EMPLOYEE_LIST",
      "HR_LOE",
      "LOE",
    ].includes(
      String(navigationFormData?.transactionCode || "").toUpperCase(),
    ) ||
    String(category || "")
      .toLowerCase()
      .includes("list-of-employees") ||
    selectedReportNameValue.includes("list of employees");

  const isListOfTrainersForm =
    [
      "HR_LIST_OF_TRAINERS",
      "LIST_OF_TRAINERS",
      "TRAINER_LIST",
      "HR_LOT",
      "LOT",
    ].includes(
      String(navigationFormData?.transactionCode || "").toUpperCase(),
    ) ||
    String(category || "")
      .toLowerCase()
      .includes("list-of-trainers") ||
    selectedReportNameValue.includes("list of trainers");

  const isHrInductionTrainingSheetForm =
    [
      "HR_INDUCTION_TRAINING_SHEET",
      "INDUCTION_TRAINING_SHEET",
      "HR_ITS",
      "ITS",
    ].includes(
      String(navigationFormData?.transactionCode || "").toUpperCase(),
    ) ||
    String(category || "")
      .toLowerCase()
      .includes("induction-training-sheet") ||
    selectedReportNameValue.includes("induction training sheet");

  const isHrTrainingNeedsRecordForm =
    [
      "HR_TRAINING_NEEDS_RECORD",
      "TRAINING_NEEDS_RECORD",
      "HR_TNR",
      "TNR",
    ].includes(
      String(navigationFormData?.transactionCode || "").toUpperCase(),
    ) ||
    String(category || "")
      .toLowerCase()
      .includes("training-needs-record") ||
    selectedReportNameValue.includes("training needs record");

  const isHrEmployeeHistoryCardForm =
    [
      "HR_EMPLOYEE_HISTORY_CARD",
      "EMPLOYEE_HISTORY_CARD",
      "HR_EHC",
      "EHC",
    ].includes(
      String(navigationFormData?.transactionCode || "").toUpperCase(),
    ) ||
    String(category || "")
      .toLowerCase()
      .includes("employee-history-card") ||
    selectedReportNameValue.includes("employee history card");

  const isSupplierEvaluationForm =
    String(navigationFormData?.transactionCode || "").toUpperCase() === "SEF" ||
    String(category || "")
      .toLowerCase()
      .includes("supplier-evaluation-form") ||
    selectedReportNameValue.includes("supplier evaluation form");

  return (
    <div className="enquiry-page">
      <div className="enquiry-page-outer-container">
        <div className="enquiry-page-container">
          {category &&
          category.toLowerCase().includes("incoming-inspection") ? (
            <IncomingInspectionInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              invoiceTableData={invoiceTableData}
              headFields={resolvedHeadFields}
            />
          ) : category &&
            category.toLowerCase().includes("in-process-inspection") ? (
            <InProcessInspectionInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              invoiceTableData={invoiceTableData}
              headFields={resolvedHeadFields}
            />
          ) : category &&
            (category.toLowerCase().includes("final-inspection") ||
              category.toLowerCase().includes("production-final")) ? (
            <FinalInspectionInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              invoiceTableData={invoiceTableData}
              headFields={resolvedHeadFields}
            />
          ) : isContractReviewForm ? (
            <ContractReviewFormLayout
              headDetails={headDetails}
              companyData={companyData}
            />
          ) : isTrainingAttendanceForm ? (
            <TrainingAttendanceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
            />
          ) : isListOfEmployeesForm ? (
            <ListOfEmployeesLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
            />
          ) : isListOfTrainersForm ? (
            <ListOfTrainersLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
            />
          ) : isHrInductionTrainingSheetForm ? (
            <HrInductionTrainingSheetLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
            />
          ) : isHrTrainingNeedsRecordForm ? (
            <HrTrainingNeedsRecordLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
            />
          ) : isHrEmployeeHistoryCardForm ? (
            <HrEmployeeHistoryCardLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
            />
          ) : isSupplierEvaluationForm ? (
            <SupplierEvaluationFormLayout
              headDetails={headDetails}
              companyData={companyData}
            />
          ) : navigationFormData.transactionCode === "ROUTE_CARD" ? (
            <RouteCardInvoiceLayout
              headDetails={headDetails}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
            />
          ) : navigationFormData.transactionCode === "PROCESS_CARD" ? (
            <ProcessCardInvoiceLayout
              headDetails={headDetails}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
            />
          ) : navigationFormData.transactionCode === "PO" ? (
            <POInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "DC" ? (
            <DCInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "PE" ? (
            <PEInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "PQ" ? (
            <PQInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "IP" ||
            navigationFormData.transactionCode === "MI" ? (
            <MaterialIssueInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "PD" ? (
            <PurchaseIndentInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "ST" ? (
            <SalesInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "JC" &&
            navigationFormData?.selectedReportType ===
              "in_process_inspection" ? (
            <InProcessInspectionInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              invoiceTableData={invoiceTableData}
            />
          ) : navigationFormData.transactionCode === "JC" &&
            navigationFormData?.selectedReportType === "final_inspection" ? (
            <FinalInspectionInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              invoiceTableData={invoiceTableData}
            />
          ) : navigationFormData.transactionCode === "JC" &&
            navigationFormData?.selectedReportType ? (
            <IncomingInspectionInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              invoiceTableData={invoiceTableData}
            />
          ) : navigationFormData.transactionCode === "JC" ? (
            <JobCardInvoiceLayout
              headDetails={headDetails}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "SQ" ? (
            <SQInvoiceLayout
              initialPrintFormat={navigationFormData.printFormat}
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "MR" ? (
            <MRInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "SE" ? (
            <SalesEnquiryLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "PI" ? (
            <PIInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "SI" ? (
            <SIInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "PT" ? (
            <PurchaseInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "GRN" ? (
            <GoodsReceiptNoteInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "JQ" ? (
            <JQInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "JI" ? (
            <JobWorkInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "WO" ? (
            <WorkOrderInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "JE" ? (
            <JobWorkEnquiryInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "JO" ? (
            <JobWorkOrderInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "JR" ? (
            <JobWorkInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "SO" ? (
            <CustomerPOInvoice
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "MRM" ? (
            <MRMInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              rowData={resolvedRowFields}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : navigationFormData.transactionCode === "VP" ? (
            <VPInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
            />
          ) : navigationFormData.transactionCode === "EC" ? (
            <ECInvoiceLayout
              headDetails={headDetails}
              companyData={companyData}
              itemDetails={itemDetails}
              invoiceTableData={invoiceTableData}
              selectedReportName={selectedReportName}
            />
          ) : (
            <>
              <div className="enquiry-header">
                <h2>
                  {capitalizeFirstLetterOfEachWord(headDetails.tran_code || "")}
                </h2>
              </div>
              <div className="enquiry-details">
                <div className="details-heding">
                  {capitalizeFirstLetterOfEachWord(category)} Details
                </div>
                <h3>
                  {convertToUpperCase(tab)} {convertToUpperCase(category)}
                </h3>
                <div className="section">
                  <div className="details-row">
                    <div className="detail-group">
                      <label className="semibold">Company Details</label>
                      <div>
                        <p>{companyData.company_name || "N/A"}</p>
                        <p>
                          <span className="semibold">Phone: </span>
                          <span className="underline">
                            {companyData.company_phone || "N/A"}
                          </span>
                        </p>
                        <p>
                          <span className="semibold underline">Address: </span>
                          {companyData.company_address || "N/A"}
                        </p>
                      </div>
                    </div>

                    <div className="detail-group">
                      <label className="semibold">Delivery Details</label>
                      <div>
                        <p>
                          <span className="semibold underline">Address: </span>
                          {companyData.delivery_address || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <h2>Transaction Details</h2>
                <div className="details-row">
                  <div className="details-group">
                    <div className="flex-direction-row">
                      <label>
                        Transaction Code - {headDetails.tran_code || "N/A"}
                      </label>
                    </div>
                    <div className="flex-direction-row">
                      <label>
                        Created Date - {formatDate(headDetails.created_at)}
                      </label>
                    </div>
                    <div className="flex-direction-row">
                      <label>Status - {headDetails.status || "N/A"}</label>
                    </div>
                  </div>

                  <div className="details-group">
                    <div className="flex-direction-row">
                      <label>
                        Updated Date - {formatDate(headDetails.updated_at)}
                      </label>
                    </div>
                    <div className="flex-direction-row">
                      <label>
                        Gross Amount - Rs{" "}
                        {formatCurrency(headDetails.gross_amt)}
                      </label>
                    </div>
                    <div className="flex-direction-row">
                      <label>
                        Net Amount - Rs {formatCurrency(headDetails.net_amt)}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="section">
                  <table className="invoice-item-table">
                    <thead>
                      <tr>
                        <th>No.</th>
                        {[...resolvedRowFields]
                          .filter((field) => !field.is_internal_field)
                          .reverse()
                          .map((field, index) => (
                            <th key={index}>
                              {field.title || field.field_name}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {itemDetails.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          {[...resolvedRowFields]
                            .filter((field) => !field.is_internal_field)
                            .reverse()
                            .map((field, idx) => (
                              <td key={idx}>{item[field.field_name] || "-"}</td>
                            ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="totals">
                    <p>
                      Gross Amount: Rs {formatCurrency(headDetails.gross_amt)}
                    </p>
                    <p>Net Amount: Rs {formatCurrency(headDetails.net_amt)}</p>
                    <div className="total-bill-separator"></div>
                    <p className="semibold">
                      Grand Total: Rs {formatCurrency(headDetails.net_amt)}
                    </p>
                  </div>

                  <div className="signature">
                    <span>Signature</span>
                    <p>Created by: {headDetails.created_by || "N/A"}</p>
                    {headDetails.approved_by && (
                      <p>Approved by: {headDetails.approved_by}</p>
                    )}
                  </div>

                  <div className="invoice-buttons">
                    <DownloadButton
                      tableData={invoiceTableData}
                      fileName="invoice"
                      downloadFormats={["excel", "pdf", "full-page-pdf"]}
                      fullPageDownload={true}
                      fullPageSelector=".enquiry-page-container"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Invoice;
